import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { getLinkSuggestions, createManualLink, tableLabel } from './dataGraphApi';
import { useCanEdit, READ_ONLY_TITLE } from 'Auth/permissions';

const PAGE_SIZE = 10;
const pct = (c) => `${Math.round((Number(c) || 0) * 100)}%`;
const keyOf = (s) => `${tableLabel(s.from)}.${s.fromColumns[0]}->${tableLabel(s.to)}.${s.toColumns[0]}`;

// Candidate joins for schemas that declare no foreign keys, so the builder above is a review
// step rather than a blank form. Collapsed until asked for: the scan is a pass over the
// snapshot's columns and there is no reason to pay for it on every page load.
const LinkSuggestionsPanel = ({ dataSourceId, graphId, selectedNode, onChanged }) => {
  const canEdit = useCanEdit();
  const [open, setOpen] = useState(false);
  const [scoped, setScoped] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [page, setPage] = useState(0);
  const [error, setError] = useState(null);

  const scopeTable = scoped && selectedNode ? selectedNode : null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getLinkSuggestions(dataSourceId, {
        table: scopeTable ? scopeTable.table : undefined,
        schema: scopeTable ? scopeTable.schema : undefined,
        graphId,
      }));
      setPage(0);
    } catch (e) {
      setError(e?.body?.message || e?.message || 'Could not load suggestions');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dataSourceId, graphId, scopeTable]);

  // Fetches on first expand, then whenever the scope changes while open.
  useEffect(() => { if (open) load(); }, [open, load]);

  const add = async (s) => {
    setBusy(keyOf(s));
    setError(null);
    try {
      await createManualLink(dataSourceId, graphId, {
        fromSchema: s.from.schema || null,
        fromTable: s.from.table,
        fromColumns: s.fromColumns,
        toSchema: s.to.schema || null,
        toTable: s.to.table,
        toColumns: s.toColumns,
        note: null,
      });
      if (onChanged) await onChanged();
      await load();
    } catch (e) {
      const msg = e?.body?.message || e?.body?.error || e?.message || 'Could not add the link';
      setError(Number(e?.status) === 409 ? 'That link already exists.' : msg);
    } finally {
      setBusy(null);
    }
  };

  const suggestions = data?.suggestions || [];
  const total = suggestions.length;
  const pageRows = suggestions.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const first = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const last = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div className='dg-panel dg-panel--links' data-testid='dg-link-suggestions'>
      <button type='button' className='dg-panel__head dg-panel__head--toggle' aria-expanded={open}
        onClick={() => setOpen(!open)} data-testid='dg-suggestions-toggle'>
        <div>
          <div className='dg-panel__title'>
            <span className='dg-caret'>{open ? '▾' : '▸'}</span> Suggested links
            {open && data?.available && ` · ${data.total}`}
          </div>
          <div className='dg-muted'>Columns that look like keys into another table. Review before adding.</div>
        </div>
      </button>

      {open && (
        <>
          <div className='dg-chips'>
            <button type='button' className={`dg-chip${scoped ? '' : ' dg-chip--on'}`}
              onClick={() => setScoped(false)} data-testid='dg-suggestions-all'>All tables</button>
            <button type='button' className={`dg-chip${scoped ? ' dg-chip--on' : ''}`}
              disabled={!selectedNode} onClick={() => setScoped(true)} data-testid='dg-suggestions-scoped'>
              {selectedNode ? `Only ${tableLabel(selectedNode)}` : 'Only the selected table'}
            </button>
          </div>

          {loading && <div className='dg-muted dg-note'>Looking for candidate links…</div>}

          {!loading && data && !data.available && (
            <div className='dg-muted dg-note' data-testid='dg-suggestions-unavailable'>{data.reason}</div>
          )}

          {!loading && data?.available && total === 0 && (
            <div className='dg-muted dg-note'>
              No suggestions — every shared key column here is already linked.
            </div>
          )}

          {!loading && data?.available && total > 0 && (
            <div className='dg-table' role='table' aria-label='Suggested links'>
              <div className='dg-table__head dg-table__head--suggestions' role='row'>
                <span>Suggested link</span><span>Why</span><span>Match</span><span />
              </div>
              {pageRows.map((s) => (
                <div className='dg-table__row dg-table__row--suggestions' role='row' key={keyOf(s)}
                  data-testid={`dg-suggestion-${s.from.table}-${s.fromColumns[0]}`}>
                  <span>
                    <span className='dg-name'>{tableLabel(s.from)}.{s.fromColumns.join(', ')}</span>
                    <span className='dg-muted'> → </span>
                    <span className='dg-name'>{tableLabel(s.to)}.{s.toColumns.join(', ')}</span>
                    {s.toKeyKind === 'PRIMARY_KEY'
                      ? <span className='dg-badge dg-badge--pk'>PK</span>
                      : <span className='dg-badge dg-badge--pk'>UNIQUE</span>}
                  </span>
                  <span className='dg-muted'>{s.reason}</span>
                  <span className='dg-muted'>{pct(s.confidence)}</span>
                  <span className='dg-actions'>
                    <Button size='sm' variant='link' disabled={Boolean(busy) || !canEdit} onClick={() => add(s)}
                      title={!canEdit ? READ_ONLY_TITLE : undefined}
                      data-testid={`dg-suggestion-add-${s.from.table}-${s.fromColumns[0]}`}>
                      {busy === keyOf(s) ? 'Adding…' : 'Add link'}
                    </Button>
                  </span>
                </div>
              ))}
            </div>
          )}

          {total > PAGE_SIZE && (
            <div className='dg-pager'>
              <button type='button' className='dg-link' disabled={page === 0} onClick={() => setPage(page - 1)}>‹</button>
              <span className='dg-muted'>{first}–{last} of {total}</span>
              <button type='button' className='dg-link' disabled={last >= total} onClick={() => setPage(page + 1)}>›</button>
            </div>
          )}

          {data?.truncated && (
            <div className='dg-muted dg-note'>
              Showing the {total} strongest of many. Pick a table to narrow the list.
            </div>
          )}

          {error && <div className='dg-error' role='alert'>{error}</div>}
        </>
      )}
    </div>
  );
};

export default LinkSuggestionsPanel;
